import React, {
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect
} from 'react';
import dynamic from 'next/dynamic';
import { SelectProps, SelectHorizontalAlignmentEnum } from './types';
import { Popover } from '../Popover';
import {
  ArrowIcon,
  ModalContent,
  modalStyles,
  SelectContainer,
  selectHorizontalAlignment,
  SelectText,
  TextsContainer
} from './styled';
import Icon from '../../Icon';
import ArrowRight from '../../../public/static/images/item/arrow-right.svg';
import useScreenSize from '../../../hooks/useScreenSize';
import { Modal } from '../Modal';
import { BackButtonIconEnum } from '../PageHeader/types';
import { useClient } from '../../../hooks/useClient';

const SelectLabel = dynamic(
  () => import('./styled').then(mod => mod.SelectLabel),
  {
    ssr: false
  }
);

export const Select = forwardRef(
  (
    {
      children,
      value,
      placeholder,
      cypress,
      width,
      stat,
      horizontalAlign = SelectHorizontalAlignmentEnum.Left,
      showIcon = true,
      styles,
      onClose,
      modalTitle,
      isPlaceholderVisible = false,
      valueTag,
      showModalHeaderBorder = true,
      popoverStyles,
      stylesModal,
      closeIcon,
      closeButtonAlign,
      input,
      error,
      modalCypress,
      inputRef,
      ariaLabel,
      isWithHover = true
    }: SelectProps,
    ref
  ) => {
    const isClient = useClient();
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const { isSmall } = useScreenSize();

    const handleFilterClick = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      if (e?.target === inputRef?.current) {
        return;
      }

      setIsOpen(!isOpen);
      if (isOpen) onClose?.();
    };

    const handleClose = () => {
      setIsOpen(false);
      onClose?.();
    };

    useEffect(() => {
      if (!isOpen) {
        onClose?.();
      }
    }, [isOpen]);

    useImperativeHandle(ref, () => ({
      closePopover: handleClose
    }));

    useEffect(() => {
      if (isOpen && inputRef?.current && !isSmall) {
        inputRef?.current.focus();
      }
    }, [inputRef?.current, isSmall, isOpen]);

    return (
      <SelectContainer
        ref={triggerRef}
        $isSelected={isOpen}
        type="button"
        onClick={handleFilterClick}
        data-cy={cypress}
        $hasValue={!!value}
        data-stat={stat}
        $styles={styles}
        $isError={!!error}
        aria-label={ariaLabel || 'Select'}
        $isWithHover={isWithHover}
      >
        <TextsContainer>
          {isPlaceholderVisible && ((isSmall && value) || !isSmall) && (
            <SelectLabel
              $isError={!!error}
              data-cy={`${cypress}-label`}
              tag={value ? 'body_2' : 'body_3'}
            >
              {placeholder}
            </SelectLabel>
          )}
          {(isOpen || value) && input}
          {!input && (
            <SelectText
              $isError={!!error}
              $hasValue={!!value}
              tag={valueTag || 'body_3'}
            >
              {value || placeholder}
            </SelectText>
          )}
        </TextsContainer>

        {showIcon && (
          <ArrowIcon $isSelected={isOpen} className="arrow-icon">
            <Icon
              indent={false}
              height={14}
              width={7}
              IconComponent={ArrowRight}
            />
          </ArrowIcon>
        )}
        {isClient &&
          (!isSmall ? (
            <Popover
              triggerRef={triggerRef}
              width={width}
              horizontalAlign={selectHorizontalAlignment(horizontalAlign)}
              isOpen={isOpen}
              closeHandler={handleClose}
              cypress={cypress}
              styles={popoverStyles}
            >
              {children}
            </Popover>
          ) : (
            <Modal
              isOpen={isOpen}
              onClose={handleClose}
              modalId={modalCypress}
              cypress={modalCypress}
              modalStyles={modalStyles(stylesModal)}
              title={modalTitle}
              closeButtonAlign={closeButtonAlign || 'right'}
              headerIcon={closeIcon || BackButtonIconEnum.Close}
              showHeaderBorder={showModalHeaderBorder}
              lockScroll={isSmall}
            >
              <ModalContent>{children}</ModalContent>
            </Modal>
          ))}
      </SelectContainer>
    );
  }
);

Select.displayName = 'Select';
